import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BootstrapData, SelfPunchInput, SelfPunchResult } from "@czasomat/contracts";
import { ApiError, post } from "./api/client";

type Attempt = { input: SelfPunchInput; employeeId: string; uncertain: boolean };
type Session = { owner: string; active: boolean; pending: boolean; attempt?: Attempt; error: string; success: string; refreshFailed: boolean };

export function useSelfPunch(data: BootstrapData) {
  const queryClient = useQueryClient();
  const [, render] = useState(0);
  const owner = `${data.auth.id}|${data.auth.employeeId ?? ""}`;
  const session = useRef<Session | null>(null);
  if (!session.current || session.current.owner !== owner) {
    if (session.current) session.current.active = false;
    session.current = { owner, active: true, pending: false, error: "", success: "", refreshFailed: false };
  }
  const current = session.current;
  useEffect(() => { current.active = true; return () => { current.active = false; }; }, [current]);
  const update = () => { if (current.active && session.current === current) render((n) => n + 1); };
  const alive = () => current.active && session.current === current;
  const refresh = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] }, { throwOnError: true });
      if (alive()) current.refreshFailed = false;
    } catch {
      if (alive()) { current.refreshFailed = true; current.error = "Nie udało się odświeżyć stanu RCP. Odśwież stan przed kolejnym odbiciem."; }
    }
  };
  const refreshState = async () => {
    if (current.pending) return;
    current.pending = true; current.error = ""; update();
    await refresh();
    if (alive()) { current.pending = false; update(); }
  };
  const punch = async () => {
    if (current.pending || current.refreshFailed) return;
    if (!current.attempt) {
      if (!data.auth.employeeId || !data.selfPunch?.available || !data.selfPunch.nextType || !data.selfPunch.stateVersion) return;
      current.attempt = { employeeId: data.auth.employeeId, uncertain: false, input: {
        type: data.selfPunch.nextType, expectedStateVersion: data.selfPunch.stateVersion, idempotencyKey: `web-${crypto.randomUUID()}`
      } };
    }
    const attempt = current.attempt;
    current.pending = true; current.error = ""; current.success = ""; update();
    try {
      const result = await post<SelfPunchResult>("/api/v1/self/punch", attempt.input);
      if (!alive()) return;
      const event = result?.event;
      if (result?.ok !== true || typeof event?.id !== "string" || !event.id.trim() || event.employeeId !== attempt.employeeId || event.type !== attempt.input.type || event.idempotencyKey !== attempt.input.idempotencyKey || typeof event.occurredAt !== "string" || !Number.isFinite(Date.parse(event.occurredAt))) {
        throw new Error("Brak zgodnego potwierdzenia odbicia. Sprawdź wynik tej samej próby.");
      }
      current.attempt = undefined;
      current.success = attempt.input.type === "WORK_START" ? "Rozpoczęcie pracy zapisane." : "Zakończenie pracy zapisane.";
      await refresh();
    } catch (error) {
      if (!alive()) return;
      const definite = error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 425, 429].includes(error.status);
      current.error = error instanceof TypeError ? "Nie udało się połączyć z serwerem. Wynik odbicia wymaga potwierdzenia." : error instanceof Error ? error.message : "Nie udało się potwierdzić odbicia.";
      if (attempt.uncertain || !definite) { attempt.uncertain = true; }
      else { current.attempt = undefined; await refresh(); }
    } finally {
      if (alive()) { current.pending = false; update(); }
    }
  };
  return {
    punch, refreshState, pending: current.pending, uncertain: Boolean(current.attempt?.uncertain),
    error: current.error, success: current.success, refreshFailed: current.refreshFailed,
    available: Boolean(data.auth.employeeId && data.selfPunch?.available)
  };
}
